import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { faHistory } from '@fortawesome/free-solid-svg-icons';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { IUserEventLog } from '@simplyfly/shared';
import {
  AlertService,
  EnumAlertOptionSize,
  EnumAlertOptionTextAlign
} from 'src/app/services/alert/alert.service';
import { IUser } from '@simplyfly/shared';
import { getUserFullname } from 'src/app/interfaces/user.helpers';
import { UserService } from 'src/app/services/user/user.service';
import {
  UserEventLogCursor,
  UserEventLogsService
} from 'src/app/services/user-event-logs/user-event-logs.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DatePipe } from '@angular/common';

interface IRouteAccessLogDetailItem {
  labelKey: string;
  value: string;
}

@Component({
    selector: 'app-header-menu-current-url-logs',
    templateUrl: './header-menu-current-url-logs.component.html',
    styleUrls: ['./header-menu-current-url-logs.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FaIconComponent, DatePipe, TranslateModule]
})
export class HeaderMenuCurrentUrlLogsComponent implements OnChanges {
  @Input() currentRoute = '/';
  @Input() currentUserId: string | null = null;

  faHistory = faHistory;

  loadingCurrentUrlLogs = false;
  loadingMoreCurrentUrlLogs = false;
  hasMoreCurrentUrlLogs = false;
  currentUrlLogs: IUserEventLog[] = [];
  currentUrlLogsError: string | null = null;

  private readonly pageSize: number = 10;
  private lastLoadedRoutePath: string | null = null;
  private routeLogsCursor: UserEventLogCursor = null;
  private currentSessionId: string | null = null;
  private currentSessionBadgeLogId: string | null = null;
  private readonly userNameByUserId = new Map<string, string>();
  private readonly externalLinkIconHtml = icon(faUpRightFromSquare).html.join('');

  constructor(
    private userEventLogsService: UserEventLogsService,
    private userService: UserService,
    private alertService: AlertService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.currentUserId) {
      this.updateCurrentSessionBadgeLogId();
    }

    if (changes.currentRoute) {
      void this.loadCurrentUrlLogs(false);
    }
  }

  async refreshCurrentUrlLogs(): Promise<void> {
    await this.loadCurrentUrlLogs(true);
  }

  async onLogsListScroll(event: Event): Promise<void> {
    if (
      this.loadingCurrentUrlLogs ||
      this.loadingMoreCurrentUrlLogs ||
      !this.hasMoreCurrentUrlLogs
    ) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (!target) {
      return;
    }

    const thresholdPx = 80;
    const scrolledToBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - thresholdPx;

    if (!scrolledToBottom) {
      return;
    }

    await this.loadMoreCurrentUrlLogs();
  }

  trackByLogId(_: number, eventLog: IUserEventLog): string {
    return eventLog.id;
  }

  getCurrentRoutePath(): string {
    const route = `${this.currentRoute || ''}`.trim();

    if (!route) {
      return '/';
    }

    if (/^https?:\/\//i.test(route)) {
      try {
        return new URL(route).pathname || '/';
      } catch {
        return '/';
      }
    }

    const routeWithoutQuery = route.split('?')[0].split('#')[0];

    if (!routeWithoutQuery) {
      return '/';
    }

    return routeWithoutQuery.startsWith('/') ? routeWithoutQuery : `/${routeWithoutQuery}`;
  }

  getEventTimestamp(eventLog: IUserEventLog): Date | null {
    return eventLog.createdAt || eventLog.clientTimestamp || null;
  }

  getEventPageDurationLabel(eventLog: IUserEventLog): string | null {
    if (typeof eventLog.pageDurationMs !== 'number' || Number.isNaN(eventLog.pageDurationMs)) {
      return null;
    }

    let diffMs = Math.max(0, Math.round(eventLog.pageDurationMs));

    const dayMs = 24 * 60 * 60 * 1000;
    const hourMs = 60 * 60 * 1000;
    const minuteMs = 60 * 1000;
    const secondMs = 1000;

    const days = Math.floor(diffMs / dayMs);
    diffMs -= days * dayMs;

    const hours = Math.floor(diffMs / hourMs);
    diffMs -= hours * hourMs;

    const minutes = Math.floor(diffMs / minuteMs);
    diffMs -= minutes * minuteMs;

    const seconds = diffMs > 0 ? Math.ceil(diffMs / secondMs) : 0;

    if (days > 0) {
      return `${days}d ${hours}h`;
    }

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }

    return `${seconds}s`;
  }

  getUserDisplayName(userId: string): string {
    return this.userNameByUserId.get(userId) || userId;
  }

  isCurrentUserCurrentSessionLog(eventLog: IUserEventLog): boolean {
    if (!this.currentUserId || !this.currentSessionId || !this.currentSessionBadgeLogId) {
      return false;
    }

    return (
      eventLog.id === this.currentSessionBadgeLogId &&
      eventLog.userId === this.currentUserId &&
      eventLog.sessionId === this.currentSessionId
    );
  }

  openRouteAccessLogDetails(eventLog: IUserEventLog): void {
    const detailItems = this.getEventDetailItems(eventLog);
    const eventTimestamp = this.getEventTimestamp(eventLog);
    const urlValue = this.normalizeDetailValue(eventLog.routeFull);
    const urlHref = this.getEventRouteHref(eventLog.routeFull);
    const detailValueByKey = new Map<string, string>();
    detailItems.forEach(detailItem => {
      detailValueByKey.set(detailItem.labelKey, detailItem.value);
    });

    const navigationRows: string[] = [
      ...(eventTimestamp
        ? [
            this.getAlertDetailRow(
              this.translate.instant('GENERAL.TIME'),
              eventTimestamp.toLocaleString()
            )
          ]
        : []),
      ...(urlValue
        ? [
            this.getAlertDetailRow(
              this.translate.instant('GENERAL.URL'),
              urlValue,
              urlHref || undefined
            )
          ]
        : []),
      ...this.getAlertDetailRowsByKeys(detailValueByKey, [
        'USERS.PREVIOUS_ROUTE',
        'USERS.TIME_ON_PAGE'
      ])
    ];

    const sessionRows = this.getAlertDetailRowsByKeys(detailValueByKey, [
      'USERS.SESSION_ID',
      'USERS.TAB_ID'
    ]);
    const locationRows = this.getAlertDetailRowsByKeys(detailValueByKey, [
      'USERS.IP_ADDRESS',
      'USERS.LOCATION',
      'USERS.COORDINATES'
    ]);
    const deviceRows = this.getAlertDetailRowsByKeys(detailValueByKey, [
      'USERS.OS',
      'USERS.BROWSER',
      'USERS.DEVICE_TYPE'
    ]);
    const displayRows = this.getAlertDetailRowsByKeys(detailValueByKey, [
      'USERS.SCREEN',
      'USERS.VIEWPORT',
      'USERS.DEVICE_RESOLUTION'
    ]);
    const technicalRows = this.getAlertDetailRowsByKeys(detailValueByKey, [
      'USERS.LANGUAGE',
      'USERS.TIMEZONE',
      'USERS.USER_AGENT'
    ]);

    const sectionCards: string[] = [
      this.getAlertDetailSectionCard('Navigation', navigationRows),
      this.getAlertDetailSectionCard('Location & Network', locationRows),
      this.getAlertDetailSectionCard('Device', deviceRows),
      this.getAlertDetailSectionCard('Display', displayRows),
      this.getAlertDetailSectionCard('Technical', technicalRows),
      this.getAlertDetailSectionCard('Session', sessionRows)
    ].filter(sectionHtml => !!sectionHtml);

    const alertBody = sectionCards.length
      ? `
        <div class="text-start container-fluid px-0">
          <div class="row g-3">
            ${sectionCards.join('')}
          </div>
        </div>
      `
      : `<div class="text-start">${this.escapeHtml(this.translate.instant('GENERAL.NO_DATA_TO_DISPLAY'))}</div>`;

    this.alertService.presentAlert({
      size: EnumAlertOptionSize.extraLarge,
      header: {
        display: true,
        title: this.translate.instant('USERS.USER_JOURNEY_LOG'),
        closeBtn: true
      },
      body: {
        text: alertBody,
        textAlign: EnumAlertOptionTextAlign.left
      }
    });
  }

  private getEventLocationLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (!eventLog) {
      return null;
    }

    const locationParts: string[] = [];

    if (eventLog.geo?.city) {
      locationParts.push(eventLog.geo.city);
    }
    if (eventLog.geo?.region) {
      locationParts.push(eventLog.geo.region);
    }
    if (eventLog.geo?.country) {
      locationParts.push(eventLog.geo.country);
    }

    return locationParts.length ? locationParts.join(', ') : null;
  }

  private getEventCoordinatesLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (
      !eventLog?.geo ||
      typeof eventLog.geo.latitude !== 'number' ||
      Number.isNaN(eventLog.geo.latitude) ||
      typeof eventLog.geo.longitude !== 'number' ||
      Number.isNaN(eventLog.geo.longitude)
    ) {
      return null;
    }

    return `${eventLog.geo.latitude}, ${eventLog.geo.longitude}`;
  }

  private getEventOsLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (!eventLog?.osName) {
      return null;
    }

    return eventLog.osVersion ? `${eventLog.osName} ${eventLog.osVersion}` : eventLog.osName;
  }

  private getEventBrowserLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (eventLog?.browserName) {
      return eventLog.browserVersion
        ? `${eventLog.browserName} ${eventLog.browserVersion}`
        : eventLog.browserName;
    }

    if (!eventLog?.userAgent) {
      return null;
    }

    return this.getBrowserLabelFromUserAgent(eventLog.userAgent);
  }

  private getEventScreenLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (!eventLog?.screen?.width || !eventLog?.screen?.height) {
      return null;
    }

    return `${eventLog.screen.width}x${eventLog.screen.height}`;
  }

  private getEventViewportLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (!eventLog?.viewport?.width || !eventLog?.viewport?.height) {
      return null;
    }

    return `${eventLog.viewport.width}x${eventLog.viewport.height}`;
  }

  private getEventDeviceResolutionLabel(eventLog: IUserEventLog | null | undefined): string | null {
    if (!eventLog?.screen?.width || !eventLog?.screen?.height) {
      return null;
    }

    const pixelRatio = eventLog.screen.pixelRatio || 1;

    const deviceWidth = Math.round(eventLog.screen.width * pixelRatio);
    const deviceHeight = Math.round(eventLog.screen.height * pixelRatio);

    return `${deviceWidth}x${deviceHeight}`;
  }

  private getEventRouteHref(routeFull: string | null | undefined): string | null {
    const route = `${routeFull || ''}`.trim();

    if (!route) {
      return null;
    }

    if (/^https?:\/\//i.test(route)) {
      return route;
    }

    return route.startsWith('/') ? route : `/${route}`;
  }

  private getEventDetailItems(eventLog: IUserEventLog): IRouteAccessLogDetailItem[] {
    const detailItems: Array<IRouteAccessLogDetailItem | null> = [
      {
        labelKey: 'USERS.PREVIOUS_ROUTE',
        value: this.normalizeDetailValue(eventLog.previousRoutePath) || ''
      },
      {
        labelKey: 'USERS.IP_ADDRESS',
        value: this.normalizeDetailValue(eventLog.ipAddress) || ''
      },
      {
        labelKey: 'USERS.LOCATION',
        value: this.normalizeDetailValue(this.getEventLocationLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.COORDINATES',
        value: this.normalizeDetailValue(this.getEventCoordinatesLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.OS',
        value: this.normalizeDetailValue(this.getEventOsLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.BROWSER',
        value: this.normalizeDetailValue(this.getEventBrowserLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.DEVICE_TYPE',
        value: this.normalizeDetailValue(eventLog.deviceType) || ''
      },
      {
        labelKey: 'USERS.SCREEN',
        value: this.normalizeDetailValue(this.getEventScreenLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.VIEWPORT',
        value: this.normalizeDetailValue(this.getEventViewportLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.DEVICE_RESOLUTION',
        value: this.normalizeDetailValue(this.getEventDeviceResolutionLabel(eventLog)) || ''
      },
      {
        labelKey: 'USERS.LANGUAGE',
        value: this.normalizeDetailValue(eventLog.language) || ''
      },
      {
        labelKey: 'USERS.TIMEZONE',
        value: this.normalizeDetailValue(eventLog.timezone) || ''
      },
      {
        labelKey: 'USERS.USER_AGENT',
        value: this.normalizeDetailValue(eventLog.userAgent) || ''
      },
      {
        labelKey: 'USERS.SESSION_ID',
        value: this.normalizeDetailValue(eventLog.sessionId) || ''
      },
      {
        labelKey: 'USERS.TAB_ID',
        value: this.normalizeDetailValue(eventLog.tabId) || ''
      },
      {
        labelKey: 'USERS.TIME_ON_PAGE',
        value: this.normalizeDetailValue(this.getEventPageDurationLabel(eventLog)) || ''
      }
    ];

    return detailItems.filter(
      (detailItem): detailItem is IRouteAccessLogDetailItem =>
        !!detailItem && this.normalizeDetailValue(detailItem.value) !== null
    );
  }

  private getAlertDetailRowsByKeys(
    detailValueByKey: Map<string, string>,
    detailKeys: string[]
  ): string[] {
    const rows: string[] = [];

    detailKeys.forEach(detailKey => {
      const value = detailValueByKey.get(detailKey);

      if (!value) {
        return;
      }

      rows.push(this.getAlertDetailRow(this.translate.instant(detailKey), value));
    });

    return rows;
  }

  private getAlertDetailSectionCard(sectionTitle: string, rows: string[]): string {
    if (!rows.length) {
      return '';
    }

    return `
      <div class="col-12 col-lg-6">
        <div class="card h-100 border bg-white shadow-sm">
          <div class="card-header bg-light fw-semibold py-2 px-3">${this.escapeHtml(sectionTitle)}</div>
          <div class="list-group list-group-flush">
            ${rows.join('')}
          </div>
        </div>
      </div>
    `;
  }

  private normalizeDetailValue(value: string | null | undefined): string | null {
    const normalizedValue = `${value || ''}`.trim();

    return normalizedValue ? normalizedValue : null;
  }

  private getAlertDetailRow(label: string, value: string, href?: string): string {
    const safeLabel = this.escapeHtml(label);
    const safeValue = this.escapeHtml(value);
    const valueContent = href
      ? `
        <span class="text-break">${safeValue}</span>
        <a
          href="${this.escapeHtml(href)}"
          target="_blank"
          rel="noopener noreferrer"
          class="d-inline-flex align-items-center ms-2 text-decoration-none"
          title="Open URL in a new tab"
          aria-label="Open URL in a new tab"
        >
          ${this.externalLinkIconHtml}
        </a>
      `
      : `<span class="text-break">${safeValue}</span>`;

    return `
      <div class="list-group-item px-3 py-2">
        <div class="text-muted small fw-semibold">${safeLabel}</div>
        <div class="mt-1 font-monospace">${valueContent}</div>
      </div>
    `;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getBrowserLabelFromUserAgent(userAgent: string): string | null {
    const matchers: Array<{ name: string; regex: RegExp }> = [
      { name: 'Edge', regex: /(EdgA|EdgiOS|Edg|Edge)\/([\d.]+)/i },
      { name: 'Opera', regex: /(OPR|Opera)\/([\d.]+)/i },
      { name: 'Samsung Internet', regex: /SamsungBrowser\/([\d.]+)/i },
      { name: 'Chrome', regex: /(Chrome|CriOS)\/([\d.]+)/i },
      { name: 'Firefox', regex: /(Firefox|FxiOS)\/([\d.]+)/i },
      { name: 'Safari', regex: /Version\/([\d.]+).*Safari\//i },
      { name: 'Internet Explorer', regex: /(?:MSIE ([\d.]+)|Trident\/.*rv:([\d.]+))/i }
    ];

    for (const matcher of matchers) {
      const match = matcher.regex.exec(userAgent);

      if (!match) {
        continue;
      }

      const version = match[2] || match[1] || null;

      return version ? `${matcher.name} ${version}` : matcher.name;
    }

    return null;
  }

  private async loadCurrentUrlLogs(forceReload: boolean): Promise<void> {
    if (this.loadingCurrentUrlLogs || this.loadingMoreCurrentUrlLogs) {
      return;
    }

    const currentRoutePath = this.getCurrentRoutePath();

    if (!forceReload && currentRoutePath === this.lastLoadedRoutePath) {
      return;
    }

    this.loadingCurrentUrlLogs = true;
    this.currentUrlLogsError = null;
    this.currentUrlLogs = [];
    this.routeLogsCursor = null;
    this.hasMoreCurrentUrlLogs = true;
    this.currentSessionBadgeLogId = null;
    this.lastLoadedRoutePath = currentRoutePath;

    try {
      await this.loadNextRouteLogsBatch(currentRoutePath);
    } catch (err: any) {
      this.currentUrlLogs = [];
      this.currentUrlLogsError = err?.message || 'Failed to load logs for current URL.';
      this.currentSessionBadgeLogId = null;
    } finally {
      this.loadingCurrentUrlLogs = false;
    }
  }

  private async loadMoreCurrentUrlLogs(): Promise<void> {
    if (
      this.loadingCurrentUrlLogs ||
      this.loadingMoreCurrentUrlLogs ||
      !this.hasMoreCurrentUrlLogs ||
      !this.lastLoadedRoutePath
    ) {
      return;
    }

    const currentRoutePath = this.getCurrentRoutePath();

    if (currentRoutePath !== this.lastLoadedRoutePath) {
      return;
    }

    this.loadingMoreCurrentUrlLogs = true;

    try {
      await this.loadNextRouteLogsBatch(currentRoutePath);
    } catch (err: any) {
      this.currentUrlLogsError = err?.message || 'Failed to load logs for current URL.';
    } finally {
      this.loadingMoreCurrentUrlLogs = false;
    }
  }

  private async loadNextRouteLogsBatch(routePath: string): Promise<void> {
    const result = await this.userEventLogsService.getPaginatedByRoutePath(
      routePath,
      this.pageSize,
      this.routeLogsCursor
    );

    if (this.lastLoadedRoutePath !== routePath) {
      return;
    }

    const existingLogIds = new Set(this.currentUrlLogs.map(eventLog => eventLog.id));
    const newEvents = result.events.filter(eventLog => !existingLogIds.has(eventLog.id));

    this.currentUrlLogs = [...this.currentUrlLogs, ...newEvents];
    this.routeLogsCursor = result.cursor;
    this.hasMoreCurrentUrlLogs = result.hasMore;
    this.currentUrlLogsError = null;

    await this.loadMissingUsersForLogs(newEvents);
    this.updateCurrentSessionBadgeLogId();
  }

  private async loadMissingUsersForLogs(currentUrlLogs: IUserEventLog[]): Promise<void> {
    const missingUserIds = Array.from(
      new Set(
        currentUrlLogs
          .map(eventLog => `${eventLog.userId || ''}`.trim())
          .filter(userId => !!userId && !this.userNameByUserId.has(userId))
      )
    );

    await Promise.all(
      missingUserIds.map(async userId => {
        try {
          const user = await firstValueFrom(this.userService.getFromId(userId));
          const displayName = this.getUserDisplayNameFromUser(user, userId);
          this.userNameByUserId.set(userId, displayName);
        } catch {
          this.userNameByUserId.set(userId, userId);
        }
      })
    );
  }

  private getUserDisplayNameFromUser(user: IUser | null | undefined, fallback: string): string {
    if (!user) {
      return fallback;
    }

    const fullName = getUserFullname(user).trim();

    if (fullName) {
      return fullName;
    }

    if (user.email) {
      return user.email;
    }

    return fallback;
  }

  private updateCurrentSessionBadgeLogId(): void {
    this.currentSessionId = this.userEventLogsService.getCurrentSessionId();
    this.currentSessionBadgeLogId = null;

    if (!this.currentUserId || !this.currentSessionId || !this.currentUrlLogs.length) {
      return;
    }

    const matchingLog = this.currentUrlLogs.find(eventLog => {
      return eventLog.userId === this.currentUserId && eventLog.sessionId === this.currentSessionId;
    });

    this.currentSessionBadgeLogId = matchingLog?.id || null;
  }
}
